// Phone Tasks (view 'inbox') — window.PhoneTasks, plus the phone task kit the
// Today page shares (window.PhoneTaskKit: row, action sheet, due/snooze
// helpers) and window.useTaskBadge (overdue + due-today count for the Tasks
// tab badge).
//
// Same data and rules as the desktop Tasks page (InboxView in app.jsx): the
// same /api/tasks params, completion through window.useTaskToggle
// (optimistic, recurring roll-forward, undo), snooze = move due_date out by
// 1 / 7 / 30 days, follow-up = a copy of the task due later, quick-add through
// window.useTaskQuickAdd (the desktop NL composer's exact path) and edit
// through window.TaskDrawer (a full-screen sheet on a phone).
//
// Task changes made here fire 'task-created' (the event every task list
// already refreshes on) — never 'atlas-data-changed', which also re-pulls the
// slow dashboard counts.

const PT_TAP = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };
const PT_DONE_GREEN = 'oklch(0.55 0.12 155)';

// ── Dates (tasks are date-only, bucketed in LOCAL time) ─────────────────────
// Local, not toISOString(): in the evening west of UTC the UTC date is
// already tomorrow, which would snooze "Tomorrow" two days out.
const ptIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const ptAddDaysIso = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return ptIso(d); };
const ptDayDiff = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
};
const ptShortDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
};
// "Today" · "Tomorrow" · "Yesterday" · "3 days overdue" · "Friday" · "Oct 20"
const ptDueLabel = (iso) => {
  const n = ptDayDiff(iso);
  if (n == null) return 'No date';
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  if (n === -1) return 'Yesterday';
  if (n < 0) return `${-n} days overdue`;
  if (n < 7) return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long' });
  return ptShortDate(iso);
};
const ptHorizon = (iso) => {
  const n = ptDayDiff(iso);
  if (n == null) return 'none';
  if (n < 0) return 'overdue';
  if (n === 0) return 'today';
  if (n < 7) return 'week';
  return 'later';
};
const PT_GROUPS = [
  ['overdue', 'Overdue'], ['today', 'Today'], ['week', 'This week'], ['later', 'Later'], ['none', 'No date'],
];
const ptGroup = (list) => {
  const by = {};
  (list || []).forEach((t) => { const h = ptHorizon(t.due_date); (by[h] = by[h] || []).push(t); });
  return PT_GROUPS.filter(([k]) => (by[k] || []).length).map(([key, label]) => ({ key, label, items: by[key] }));
};
// Open, assigned task due today or earlier — the Today list and the badge.
const ptIsDueNow = (t) => t && t.status !== 'completed' && (ptDayDiff(t.due_date) ?? 1) <= 0;

// ── Writes (same payloads as InboxView) ─────────────────────────────────────
const ptTasksChanged = () => window.dispatchEvent(new CustomEvent('task-created'));

const ptSnooze = async (t, days) => {
  const due = ptAddDaysIso(days);
  try {
    await window.AtlasApi.updateTask(t.id, { due_date: due });
    window.pushToast({ type: 'success', title: 'Snoozed', body: `Due ${window.fmtTaskDue(due)}` });
    ptTasksChanged();
  } catch (e) {
    window.pushToast({ type: 'error', title: 'Could not snooze the task', body: e.message });
  }
};

const ptFollowUp = async (t, days, activeUser) => {
  const due = ptAddDaysIso(days);
  try {
    await window.AtlasApi.createTask({
      title: t.title,
      company_id: t.company_id || undefined,
      engagement_id: t.engagement_id || undefined,
      contact_id: t.contact_id || undefined,
      assigned_to: t.assigned_to,
      created_by: activeUser?.id || undefined,
      task_type: t.task_type,
      priority: t.priority,
      due_date: due,
    });
    window.pushToast({ type: 'success', title: 'Follow-up scheduled', body: `Due ${window.fmtTaskDue(due)}` });
    ptTasksChanged();
  } catch (e) {
    window.pushToast({ type: 'error', title: 'Could not schedule the follow-up', body: e.message });
  }
};

// The task's own link (a CIM section's task), then deal, company, contact —
// the desktop row's click order.
const ptOpenLinked = (t, nav) => {
  if (t.link) { window.location.hash = '#' + t.link; return true; }
  if (t.engagement_id && nav.goToDeal) { nav.goToDeal(t.engagement_id); return true; }
  if (t.company_id && nav.goToCompany) { nav.goToCompany(t.company_id); return true; }
  if (t.contact_id && nav.goToContact) { nav.goToContact(t.contact_id); return true; }
  return false;
};

// tel:/mailto: navigate in place (a blank tab would open for them); web links
// go through the native shell when there is one so Teams/Zoom reach their app.
const ptOpenHref = (url) => {
  if (!url) return;
  if (window.AtlasNative && window.AtlasNative.openExternal) { window.AtlasNative.openExternal(url); return; }
  if (/^(tel|mailto|sms):/i.test(url)) window.location.href = url;
  else window.open(url, '_blank', 'noopener');
};

// Refetch when the app comes back to the foreground (visibilitychange covers
// both a Safari tab and the native shell's resume) and on the given window
// events. Foreground refreshes skip if the last one was under 30s ago.
const ptUseRefresh = (fn, events) => {
  const ref = React.useRef(fn);
  ref.current = fn;
  const key = (events || []).join('|');
  React.useEffect(() => {
    let last = Date.now();
    const run = () => { last = Date.now(); ref.current(); };
    const onVis = () => { if (document.visibilityState === 'visible' && Date.now() - last > 30000) run(); };
    const evs = key ? key.split('|') : [];
    evs.forEach((e) => window.addEventListener(e, run));
    document.addEventListener('visibilitychange', onVis);
    return () => {
      evs.forEach((e) => window.removeEventListener(e, run));
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [key]);
};

// The grey second line: recurrence, linked record, people, subtasks, origin.
const ptMetaParts = (t, { showAssignee, meId } = {}) => {
  const parts = [];
  if (t.recurrence_text || t.recurrence) parts.push(`↻ ${t.recurrence_text || 'repeats'}`);
  const rec = [t.contact_name, t.engagement_name || t.company_name].filter(Boolean).join(' · ');
  if (rec) parts.push(rec);
  if (showAssignee && t.assigned_to_name && t.assigned_to !== meId) parts.push(`for ${t.assigned_to_name}`);
  if ((t.co_assignee_names || []).length) parts.push(`with ${t.co_assignee_names.join(', ')}`);
  if (t.subtask_total > 0) parts.push(`${t.subtask_done}/${t.subtask_total} subtasks`);
  if (t.source === 'automation_rule') parts.push('Automatic');
  return parts;
};
const PT_PRIORITY = { urgent: { label: 'Urgent', color: 'var(--rose-fg)' }, high: { label: 'High', color: 'var(--amber-fg)' } };

// ── Round complete control (44px target, 24px circle) ───────────────────────
const PhoneTaskCheck = ({ checked, popping, onClick, label, size = 24 }) => (
  <button type="button" onClick={onClick} aria-label={label} aria-pressed={checked}
    style={{ ...PT_TAP, width: 48, minHeight: 48, alignSelf: 'stretch', flexShrink: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
    <span style={{
      width: size, height: size, borderRadius: size / 2, boxSizing: 'border-box',
      border: checked ? 'none' : '2px solid var(--border-strong)', background: checked ? PT_DONE_GREEN : 'transparent',
      color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      animation: popping ? 'atlas-check-pop .25s ease' : 'none',
    }}>{checked && <Icon.Check size={Math.round(size * 0.6)} strokeWidth={2.2} />}</span>
  </button>
);

// ── A task row ──────────────────────────────────────────────────────────────
// Check · title (two lines max) + due/meta line · optional ⋯. While a just-
// completed task lingers (useTaskToggle's 5s window) the row turns green,
// says what happened (incl. the next occurrence of a recurring task) and
// offers Undo.
const PhoneTaskRow = ({ t, toggle, onTap, onMore, showAssignee, meId }) => {
  const ps = toggle.stateFor(t.id);
  const checked = t.status === 'completed' || !!ps;
  const lingering = !!(ps && ps.phase === 'done');
  const n = ptDayDiff(t.due_date);
  const overdue = !checked && n != null && n < 0;
  const pri = PT_PRIORITY[t.priority];
  const parts = ptMetaParts(t, { showAssignee, meId });
  const doneLine = t.status === 'completed' && t.completed_at
    ? `Completed ${ptShortDate(String(t.completed_at).slice(0, 10))}` : null;
  return (
    <div style={{
      display: 'flex', alignItems: 'stretch', minHeight: 60, borderBottom: '1px solid var(--border)',
      background: lingering ? 'var(--green-bg)' : 'var(--surface)', transition: 'background .3s ease',
    }}>
      <PhoneTaskCheck checked={checked} popping={!!ps} onClick={() => toggle.toggle(t)}
        label={checked ? `Mark "${t.title}" not done` : `Complete "${t.title}"`} />
      <button type="button" onClick={() => onTap && onTap(t)}
        style={{ ...PT_TAP, flex: 1, minWidth: 0, textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: '10px 14px 10px 0', color: 'var(--text)', fontFamily: 'inherit' }}>
        <span style={{
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere',
          fontSize: 15.5, fontWeight: 500, lineHeight: 1.3,
          textDecoration: checked ? 'line-through' : 'none', color: checked ? 'var(--text-3)' : 'var(--text)',
        }}>{t.title}</span>
        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: 13, lineHeight: 1.35, color: 'var(--text-3)', marginTop: 2 }}>
          {lingering ? (
            <span style={{ color: 'var(--green-fg)', fontWeight: 600 }}>
              {ps.spawnedDue ? `Done — next one ${window.fmtTaskDue(ps.spawnedDue)}` : (t.recurrence ? 'Done' : 'Done · tap to add a follow-up')}
            </span>
          ) : (
            <>
              {pri && <span style={{ color: pri.color, fontWeight: 600 }}>{pri.label} · </span>}
              <span style={{ color: overdue ? 'var(--rose-fg)' : undefined, fontWeight: overdue ? 600 : undefined }}>
                {doneLine || ptDueLabel(t.due_date)}
              </span>
              {parts.length > 0 && ` · ${parts.join(' · ')}`}
            </>
          )}
        </span>
      </button>
      {lingering ? (
        <button type="button" onClick={() => toggle.undo(t)}
          style={{ ...PT_TAP, flexShrink: 0, minWidth: 64, padding: '0 16px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
          Undo
        </button>
      ) : onMore && (
        <button type="button" aria-label={`More for "${t.title}"`} onClick={() => onMore(t)}
          style={{ ...PT_TAP, flexShrink: 0, width: 44, border: 'none', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon.More size={18} />
        </button>
      )}
    </div>
  );
};

// ── Task action sheet ───────────────────────────────────────────────────────
// Everything you'd do with one task: read its details and notes (email drafts
// live there), complete, open what it's about, call/email its contact,
// snooze, schedule a follow-up, edit.
const PhoneTaskSheet = ({ task, onClose, toggle, activeUser, nav, onEdit }) => {
  // Keep rendering the last task through the close animation.
  const [shown, setShown] = React.useState(task);
  const [mode, setMode] = React.useState('main');   // main | snooze | followup
  const [contact, setContact] = React.useState(null);
  React.useEffect(() => {
    if (!task) return;
    setShown(task); setMode('main'); setContact(null);
  }, [task]);
  // Call / Email rows need the contact's phone and email — tasks only carry
  // the name, so fetch the contact when the sheet opens on one.
  React.useEffect(() => {
    if (!task || !task.contact_id) return undefined;
    let live = true;
    window.AtlasApi.getContact(task.contact_id)
      .then((c) => { if (live) setContact(c || null); })
      .catch(() => {});
    return () => { live = false; };
  }, [task && task.id, task && task.contact_id]);

  const t = shown;
  if (!t) return null;
  const ps = toggle.stateFor(t.id);
  const done = t.status === 'completed' || !!ps;
  const n = ptDayDiff(t.due_date);
  const go = (fn) => { onClose(); fn(); };
  const opt = (days, label) => ({ key: label, label, sub: window.fmtTaskDue(ptAddDaysIso(days)) });

  let items;
  if (mode === 'snooze') {
    items = [
      { ...opt(1, 'Tomorrow'), onClick: () => go(() => ptSnooze(t, 1)) },
      { ...opt(7, 'Next week'), onClick: () => go(() => ptSnooze(t, 7)) },
      { ...opt(30, 'Next month'), onClick: () => go(() => ptSnooze(t, 30)) },
      { key: 'back', label: 'Back', icon: <Icon.ChevronL size={18} />, onClick: () => setMode('main') },
    ];
  } else if (mode === 'followup') {
    items = [
      { ...opt(3, 'In 3 days'), onClick: () => go(() => ptFollowUp(t, 3, activeUser)) },
      { ...opt(7, 'In a week'), onClick: () => go(() => ptFollowUp(t, 7, activeUser)) },
      { ...opt(30, 'In a month'), onClick: () => go(() => ptFollowUp(t, 30, activeUser)) },
      { key: 'back', label: 'Back', icon: <Icon.ChevronL size={18} />, onClick: () => setMode('main') },
    ];
  } else {
    // DealCloud phones are free text — parsePhones (mobile/people.jsx) splits
    // several numbers, drops the "(Work)" label off the dial string and
    // dials an extension after a pause. One Call row per number, the number
    // and its label as the sub-line.
    const phones = (contact && contact.phone && window.AtlasPhone) ? window.AtlasPhone.parsePhones(contact.phone) : [];
    const email = contact && contact.email;
    const callLabel = `Call ${t.contact_name ? t.contact_name.split(' ')[0] : ''}`.trim();
    items = [
      ps && ps.phase === 'done'
        ? { key: 'undo', label: 'Undo complete', icon: <Icon.Refresh size={18} />, onClick: () => go(() => toggle.undo(t)) }
        : { key: 'toggle', label: done ? 'Mark not done' : 'Complete', icon: <Icon.Check size={18} />, disabled: !!ps, onClick: () => go(() => toggle.toggle(t)) },
      t.engagement_id && { key: 'deal', label: t.engagement_name || 'Open deal', sub: 'Deal', icon: <Icon.Pipeline size={18} />, onClick: () => go(() => nav.goToDeal(t.engagement_id)) },
      t.company_id && { key: 'company', label: t.company_name || 'Open company', sub: 'Company', icon: <Icon.Building size={18} />, onClick: () => go(() => nav.goToCompany(t.company_id)) },
      t.contact_id && { key: 'contact', label: t.contact_name || 'Open contact', sub: 'Contact', icon: <Icon.User size={18} />, onClick: () => go(() => nav.goToContact(t.contact_id)) },
      ...phones.map((p, i) => ({
        key: `call-${i}`, label: callLabel, sub: p.label ? `${p.display} · ${p.label}` : p.display,
        icon: <Icon.Phone size={18} />, onClick: () => ptOpenHref(p.tel),
      })),
      email && { key: 'email', label: `Email ${t.contact_name ? t.contact_name.split(' ')[0] : ''}`.trim(), sub: email, icon: <Icon.Mail size={18} />, onClick: () => ptOpenHref(`mailto:${email}`) },
      !done && { key: 'snooze', label: 'Snooze', sub: 'Tomorrow, next week or next month', icon: <Icon.Clock size={18} />, onClick: () => setMode('snooze') },
      !t.recurrence && { key: 'followup', label: 'Add follow-up', sub: 'A copy of this task, due later', icon: <Icon.Plus size={18} />, onClick: () => setMode('followup') },
      onEdit && { key: 'edit', label: 'Edit task', sub: 'Title, due date, assignees, repeat, notes', icon: <Icon.Edit size={18} />, onClick: () => { onClose(); onEdit(t); } },
    ];
  }

  const detail = (label, value, color) => value ? (
    <div style={{ display: 'flex', gap: 12, padding: '5px 0', fontSize: 14.5, lineHeight: 1.4 }}>
      <span style={{ width: 92, flexShrink: 0, color: 'var(--text-3)' }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, color: color || 'var(--text)', overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  ) : null;
  const notes = (t.notes || '').trim();
  const subs = t.subtasks || [];

  return (
    <window.PhoneSheet open={!!task} onClose={onClose} title={mode === 'snooze' ? 'Snooze until' : mode === 'followup' ? 'Follow up' : 'Task'}>
      {mode === 'main' && (
        <div style={{ padding: '0 18px 10px' }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', lineHeight: 1.35, overflowWrap: 'anywhere', textDecoration: done ? 'line-through' : 'none' }}>{t.title}</div>
          <div style={{ marginTop: 8 }}>
            {detail('Due', n == null ? 'No date'
              : `${window.fmtTaskDue(t.due_date)}${n < 0 ? ` · ${-n} day${n === -1 ? '' : 's'} overdue` : n === 0 ? ' · today' : n === 1 ? ' · tomorrow' : ''}`,
            n != null && n < 0 && !done ? 'var(--rose-fg)' : null)}
            {detail('Repeats', t.recurrence_text || (t.recurrence ? 'Yes' : null))}
            {detail('Priority', t.priority && t.priority !== 'normal' ? t.priority[0].toUpperCase() + t.priority.slice(1) : null)}
            {detail('Assigned to', t.assigned_to_name)}
            {detail('With', (t.co_assignee_names || []).join(', ') || null)}
            {detail('Created by', t.source === 'automation_rule' ? `Atlas automatically${t.rule_key ? ` (${t.rule_key.replace(/_/g, ' ')})` : ''}` : null)}
          </div>
          {notes && (
            <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--border)', fontSize: 14.5, lineHeight: 1.5, color: 'var(--text-2)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: '32vh', overflowY: 'auto' }}>
              {notes}
            </div>
          )}
          {subs.length > 0 && (
            <div style={{ marginTop: 10, borderTop: '1px solid var(--border)' }}>
              {subs.map((s) => {
                const sps = toggle.stateFor(s.id);
                const sDone = s.status === 'completed' || !!sps;
                return (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', minHeight: 48, borderBottom: '1px solid var(--border)' }}>
                    <PhoneTaskCheck checked={sDone} popping={!!sps} size={22} onClick={() => toggle.toggle(s)}
                      label={sDone ? `Mark "${s.title}" not done` : `Complete "${s.title}"`} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 15, color: sDone ? 'var(--text-3)' : 'var(--text)', textDecoration: sDone ? 'line-through' : 'none', overflowWrap: 'anywhere' }}>{s.title}</span>
                    {sps && sps.phase === 'done' && (
                      <button type="button" onClick={() => toggle.undo(s)} style={{ ...PT_TAP, height: 44, padding: '0 12px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Undo</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {mode !== 'main' && (
        <div style={{ padding: '0 18px 6px', fontSize: 14, color: 'var(--text-3)', overflowWrap: 'anywhere' }}>{t.title}</div>
      )}
      <div style={{ borderTop: '1px solid var(--border)' }}>
        <window.PhoneActionList items={items} />
      </div>
    </window.PhoneSheet>
  );
};

// ── Tasks-tab badge: open tasks of mine due today or earlier ────────────────
// Same request as Home / the desktop bell (apiFetch shares it), refreshed on
// task changes, on return to the app and every 5 minutes.
const useTaskBadge = (activeUser) => {
  const uid = (activeUser && activeUser.id) || null;
  const [count, setCount] = React.useState(null);
  const load = React.useCallback(() => {
    if (!uid) { setCount(null); return; }
    window.AtlasApi.listTasks({ assigned_to: uid, status: 'open', limit: 100 })
      .then((res) => setCount(((res && res.items) || []).filter(ptIsDueNow).length))
      .catch(() => {});
  }, [uid]);
  React.useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);
  ptUseRefresh(load, ['task-created']);
  return count;
};

// ── Quick add (natural language) ────────────────────────────────────────────
const PhoneTaskQuickAdd = ({ activeUser, onCreated }) => {
  const inputRef = React.useRef(null);
  const q = window.useTaskQuickAdd({ activeUser, onCreated, inputRef });
  const people = (q.draft && q.draft.contact_candidates) || [];
  const firms = (q.draft && q.draft.company_candidates) || [];
  const chip = (label, key) => (
    <span key={key} style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 999, background: 'var(--surface)', border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-2)' }}>{label}</span>
  );
  const d = q.draft;
  return (
    <div style={{ padding: '12px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input ref={inputRef} value={q.text} disabled={q.busy}
          enterKeyHint={d ? 'done' : 'go'} autoCapitalize="sentences"
          placeholder={'Add a task — "call @Boris Friday"'}
          onChange={(e) => {
            q.setText(e.target.value);
            q.trackMention(e.target.value, e.target.selectionStart);
            if (q.draft || q.error) q.reset();
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (q.mention && q.mentionHits.length) { q.pickMention(q.mentionHits[0]); return; }
            if (d) q.confirm(); else q.parse();
          }}
          onBlur={() => setTimeout(q.closeMention, 150)}
          style={{ flex: 1, minWidth: 0, height: 44, padding: '0 12px', fontSize: 16, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', outline: 'none', WebkitAppearance: 'none' }} />
        {!d && (
          <button type="button" onClick={q.parse} disabled={q.busy || !q.text.trim()}
            style={{ ...PT_TAP, height: 44, padding: '0 16px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: q.busy || !q.text.trim() ? 0.5 : 1, flexShrink: 0 }}>
            {q.busy ? 'Reading…' : 'Add'}
          </button>
        )}
      </div>

      {/* @-mention results: a tap list under the field (taps keep focus). */}
      {q.mention && q.mentionHits.length > 0 && (
        <div style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {q.mentionHits.map((h, i) => (
            <button key={`${h.entity_type}:${h.id}`} type="button"
              onMouseDown={(e) => e.preventDefault()} onClick={() => q.pickMention(h)}
              style={{ ...PT_TAP, display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 48, padding: '6px 12px', border: 'none', borderTop: i ? '1px solid var(--border)' : 'none', background: 'var(--surface)', textAlign: 'left', cursor: 'pointer', color: 'var(--text)' }}>
              <span style={{ color: 'var(--text-3)', display: 'inline-flex' }}>{h.entity_type === 'contact' ? <Icon.User size={16} /> : <Icon.Building size={16} />}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.title}</span>
                {h.subtitle && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.subtitle}</span>}
              </span>
            </button>
          ))}
        </div>
      )}

      {q.error && <div style={{ marginTop: 8, fontSize: 13.5, color: 'var(--rose-fg)' }}>{q.error}</div>}

      {d && (
        <div style={{ marginTop: 10, padding: 12, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', overflowWrap: 'anywhere' }}>{d.title}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {chip(`Due ${window.fmtTaskDue(d.due_date)}`, 'due')}
            {d.recurrence_text && chip(`↻ ${d.recurrence_text}`, 'rec')}
            {d.priority && d.priority !== 'normal' && chip(d.priority[0].toUpperCase() + d.priority.slice(1), 'pri')}
            {d.task_type && chip(d.task_type.replace(/_/g, ' '), 'type')}
            {d.assigned_to_name && d.assigned_to !== (activeUser && activeUser.id) && chip(`For ${d.assigned_to_name}`, 'who')}
            {(d.co_assignee_names || []).length > 0 && chip(`With ${d.co_assignee_names.join(', ')}`, 'co')}
          </div>
          {people.length > 0 && <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: -4 }}>Contact</div>}
          {people.length > 0 && (
            <Select
              value={String(q.contactIdx)}
              onChange={(e) => {
                const idx = Number(e.target.value);
                q.setContactIdx(idx);
                // Follow the person's employer when it's on offer.
                const p = idx >= 0 ? people[idx] : null;
                if (p && p.company_id) {
                  const j = firms.findIndex((c) => c.company_id === p.company_id);
                  if (j >= 0) q.setCompanyIdx(j);
                }
              }}
              options={[
                ...people.map((p, i) => ({ value: String(i), label: `${p.contact_name}${p.company_name ? ` — ${p.company_name}` : ''}` })),
                { value: '-1', label: 'No contact link' },
              ]}
              style={{ width: '100%' }}
            />
          )}
          {firms.length > 0 && <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: -4 }}>Company</div>}
          {firms.length > 0 && (
            <Select
              value={String(q.companyIdx)}
              onChange={(e) => q.setCompanyIdx(Number(e.target.value))}
              options={[
                ...firms.map((c, i) => ({ value: String(i), label: c.company_name })),
                { value: '-1', label: 'No company link' },
              ]}
              style={{ width: '100%' }}
            />
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={q.reset} disabled={q.busy}
              style={{ ...PT_TAP, flex: 1, height: 44, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 15, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
            <button type="button" onClick={q.confirm} disabled={q.busy}
              style={{ ...PT_TAP, flex: 2, height: 44, borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: q.busy ? 0.6 : 1 }}>
              {q.busy ? 'Creating…' : 'Create task'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── The page ────────────────────────────────────────────────────────────────
const PT_STATUS = [
  { value: 'open', label: 'Open' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'done', label: 'Done' },
];

const PhoneTasks = ({ activeUser, teamMembers, goToDeal, goToCompany, goToContact }) => {
  const meId = activeUser && activeUser.id;
  const [scope, setScope] = React.useState('mine');     // mine | all — InboxView's tab
  const [status, setStatus] = React.useState('open');   // open | overdue | done
  const [tasks, setTasks] = React.useState(null);       // null = loading
  const [error, setError] = React.useState(null);
  const [counts, setCounts] = React.useState({ mine: null, all: null });
  const [review, setReview] = React.useState(null);     // { proposals, inbox } awaiting a decision
  const [sheetTask, setSheetTask] = React.useState(null);
  const [editing, setEditing] = React.useState(null);
  const [showLater, setShowLater] = React.useState(false);  // in-memory only (never the desktop pref)
  const [members, setMembers] = React.useState(teamMembers || []);
  const seq = React.useRef(0);
  const nav = { goToDeal, goToCompany, goToContact };

  React.useEffect(() => {
    if ((teamMembers || []).length) { setMembers(teamMembers); return; }
    window.AtlasData.loadTeamMembers().then((m) => setMembers(Array.isArray(m) ? m : [])).catch(() => {});
  }, [teamMembers]);

  // Same params, in the same order, as InboxView.reloadTasks.
  const load = React.useCallback(async (quiet) => {
    // Unknown user (whoami not back, or the API is down): keep loading
    // instead of showing "no open tasks".
    if (!meId) { setTasks(null); return; }
    const mine = ++seq.current;
    if (!quiet) { setTasks(null); setError(null); }
    const params = scope === 'all' ? { my_team: true, limit: 100 } : { assigned_to: meId, limit: 100 };
    if (status === 'open') params.status = 'open';
    if (status === 'overdue') params.overdue = true;
    if (status === 'done') params.status = 'completed';
    try {
      const res = await window.AtlasApi.listTasks(params);
      if (mine !== seq.current) return;
      setTasks((res && res.items) || []);
      setError(null);
    } catch (e) {
      if (mine !== seq.current) return;
      if (!quiet) { setTasks([]); setError(e.message || 'Could not load tasks'); }
    }
  }, [meId, scope, status]);
  React.useEffect(() => { load(false); }, [load]);

  const loadCounts = React.useCallback(() => {
    if (!meId) return;
    const total = (p) => window.AtlasApi.listTasks({ ...p, status: 'open', limit: 1 })
      .then((r) => (r && typeof r.total === 'number' ? r.total : null)).catch(() => null);
    Promise.all([total({ assigned_to: meId }), total({ my_team: true })])
      .then(([mineN, allN]) => setCounts({ mine: mineN, all: allN }));
  }, [meId]);
  React.useEffect(() => { loadCounts(); }, [loadCounts]);

  // AI-suggested tasks + forwarded emails waiting for a call — counted here,
  // decided on desktop (or in Slack, where the same buttons ride).
  const loadReview = React.useCallback(() => {
    Promise.all([
      window.AtlasApi.listTaskProposals().then((r) => (Array.isArray(r) ? r.length : 0)).catch(() => 0),
      window.AtlasApi.listInboxPending().then((r) => (Array.isArray(r) ? r.length : 0)).catch(() => 0),
    ]).then(([proposals, inbox]) => setReview({ proposals, inbox }));
  }, []);
  React.useEffect(() => { loadReview(); }, [loadReview]);

  ptUseRefresh(() => { load(true); loadCounts(); loadReview(); }, ['task-created', 'atlas-data-changed']);

  // The finished row lingers for 5s (Undo), then the list re-pulls.
  const toggle = window.useTaskToggle(ptTasksChanged);

  const moveOverdueToToday = async (list) => {
    const ok = await window.showConfirm({
      title: `Move ${list.length} overdue task${list.length === 1 ? '' : 's'} to today?`,
      body: 'Their due date becomes today. Recurring tasks keep their schedule after that.',
      confirmLabel: 'Move to today',
    });
    if (!ok) return;
    const today = ptAddDaysIso(0);
    await Promise.allSettled(list.map((t) => window.AtlasApi.updateTask(t.id, { due_date: today })));
    window.pushToast({ type: 'success', title: 'Overdue rescheduled', body: `${list.length} task(s) moved to today` });
    ptTasksChanged();
  };

  const groups = status === 'open' ? ptGroup(tasks) : [{ key: 'all', label: null, items: tasks || [] }];
  const reviewN = review ? review.proposals + review.inbox : 0;
  const row = (t) => (
    <PhoneTaskRow key={t.id} t={t} toggle={toggle} onTap={setSheetTask}
      showAssignee={scope === 'all'} meId={meId} />
  );

  return (
    <window.PhoneScroll>
      <PhoneTaskQuickAdd activeUser={activeUser} onCreated={ptTasksChanged} />

      <div style={{ padding: '12px 16px 10px' }}>
        <window.PhoneSegmented value={scope} onChange={setScope}
          options={[{ value: 'mine', label: 'Mine', count: counts.mine }, { value: 'all', label: 'Team', count: counts.all }]} />
      </div>
      <window.PhoneChips value={status} onChange={setStatus} options={PT_STATUS} style={{ paddingBottom: 4 }} />

      {reviewN > 0 && (
        <div style={{ margin: '12px 16px 0', padding: '10px 12px', borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--text-2)' }}>
          <span style={{ color: 'var(--accent-text)', display: 'inline-flex', flexShrink: 0 }}><Icon.Sparkle size={16} /></span>
          <span style={{ flex: 1, minWidth: 0, lineHeight: 1.4 }}>
            {[review.proposals ? `${review.proposals} suggested task${review.proposals === 1 ? '' : 's'}` : null,
              review.inbox ? `${review.inbox} forwarded email${review.inbox === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}
            {' '}to review on desktop
          </span>
        </div>
      )}

      {tasks === null && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--border)' }}><SkeletonRows rows={6} /></div>
      )}
      {tasks !== null && error && (
        <window.PhoneEmpty title="Couldn't load tasks" body={error}
          action={<Btn variant="outline" onClick={() => load(false)}>Try again</Btn>} />
      )}
      {tasks !== null && !error && tasks.length === 0 && (
        <window.PhoneEmpty
          title={status === 'overdue' ? 'Nothing overdue' : status === 'done' ? 'No completed tasks yet' : scope === 'all' ? 'No open tasks across your team' : 'No open tasks'}
          body={status === 'open' && scope === 'mine' ? 'Type one above — e.g. "remind me every Monday to check on Acme".' : null} />
      )}

      {tasks !== null && !error && groups.map((g) => {
        if (!g.items.length) return null;
        const collapsed = g.key === 'later' && !showLater;
        return (
          <div key={g.key}>
            {g.label ? (
              <window.PhoneSectionHeader
                title={<span style={{ color: g.key === 'overdue' ? 'var(--rose-fg)' : undefined }}>{g.label}</span>}
                count={g.items.length}
                action={g.key === 'overdue' ? (
                  <button type="button" onClick={() => moveOverdueToToday(g.items)}
                    style={{ ...PT_TAP, height: 32, margin: '-8px -8px -8px 0', padding: '0 8px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                    Move all to today
                  </button>
                ) : g.key === 'later' && !collapsed ? (
                  <button type="button" onClick={() => setShowLater(false)}
                    style={{ ...PT_TAP, height: 32, margin: '-8px -8px -8px 0', padding: '0 8px', border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                    Hide
                  </button>
                ) : null} />
            ) : <div style={{ height: 14 }} />}
            {!collapsed && <div style={{ borderTop: '1px solid var(--border)' }}>{g.items.map(row)}</div>}
            {collapsed && (
              <button type="button" onClick={() => setShowLater(true)}
                style={{ ...PT_TAP, display: 'block', width: '100%', minHeight: 48, padding: '0 16px', border: 'none', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', background: 'var(--surface)', textAlign: 'left', fontSize: 15, color: 'var(--accent-text)', cursor: 'pointer' }}>
                Show {g.items.length} task{g.items.length === 1 ? '' : 's'} due later
              </button>
            )}
          </div>
        );
      })}
      {tasks && tasks.length >= 100 && (
        <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-3)' }}>Showing the first 100 — the rest are on desktop.</div>
      )}

      <PhoneTaskSheet task={sheetTask} onClose={() => setSheetTask(null)} toggle={toggle}
        activeUser={activeUser} nav={nav} onEdit={setEditing} />
      {editing && (
        <window.TaskDrawer activeUser={activeUser} members={members} task={editing}
          onClose={() => setEditing(null)} onCreated={ptTasksChanged} />
      )}
    </window.PhoneScroll>
  );
};

window.PhoneTaskKit = {
  isoLocal: ptIso, addDaysIso: ptAddDaysIso, dayDiff: ptDayDiff, dueLabel: ptDueLabel, horizon: ptHorizon,
  group: ptGroup, isDueNow: ptIsDueNow, snooze: ptSnooze, followUp: ptFollowUp, openLinked: ptOpenLinked,
  openHref: ptOpenHref, useRefresh: ptUseRefresh, tasksChanged: ptTasksChanged,
  Row: PhoneTaskRow, Sheet: PhoneTaskSheet, Check: PhoneTaskCheck,
};
Object.assign(window, { PhoneTasks, useTaskBadge });
